import { default as pMap } from 'p-map'
import { default as string } from '@poppinss/string'
import { stripUndefinedValuesFromObject } from '../../helpers'
import { ResourcefulRouterVerb } from '../../router_macro/types'
import {
  isResourcefulModel,
  isObject,
  isInstanceOf,
  isNonArraySchemaObject,
} from '../../type_guards'
import {
  ResourcefulStringType,
  ResourcefulDateType,
  ResourcefulDateTimeType,
  ResourcefulBinaryType,
  ResourcefulNumberType,
  ResourcefulIntegerType,
  ResourcefulBigintType,
  ResourcefulUnsignedIntegerType,
  ResourcefulBooleanType,
  ResourcefulObjectType,
  ResourcefulArrayType,
  ResourcefulDataType,
} from '../../data_types'
import type { ElementCompact } from 'xml-js'
import type { OpenAPIV3 } from 'openapi-types'
import type { HttpContext } from '@adonisjs/core/http'
import type { default as ApplicationService } from '@adonisjs/core/services/app'
import type { ModelColumnOptions, LucidModel } from '@adonisjs/lucid/types/model'
import type { ResourcefulModelController } from '../controllers/resourceful_model_controller'
import type { ResourcefulRouterOptionsService } from '../../router_macro/services/resourceful_router_options_service'
import type {
  ResourcefulModelOpenApiSchema,
  ResourcefulColumnDefinition,
  ResourcefulComputedAccessorDefinition,
} from '../../types'
import type {
  DateColumnOptions,
  ResourcefulHttpVerb,
  ResourcefulModel,
  ResourcefulModelMetaSchema,
} from '@nhtio/lucid-resourceful/types'
import type {
  PropertyAttributes,
  NavigationPropertyAttributes,
  AnnotationAttributes,
  EntityTypeAttributes,
  EntitySetAttributes,
  NavigationPropertyBindingAttributes,
} from '../types'
import type {
  HasOneRelationContract,
  BelongsToRelationContract,
  HasManyRelationContract,
  HasManyThroughRelationContract,
  ManyToManyRelationContract,
} from '@adonisjs/lucid/types/relations'

type AppServiceInstance = typeof ApplicationService

type ODataModelVerbPolicy = {
  [key in ResourcefulRouterVerb]: boolean
}
type ParamSpec = { name: string; type: string; nullable: boolean }
type Boundness = 'entity' | 'collection'
type OpKind = 'action' | 'function'
type BasicResourcefulOModelScalarType =
  | 'Edm.String'
  | 'Edm.Date'
  | 'Edm.DateTimeOffset'
  | 'Edm.Binary'
  | 'Edm.Decimal'
  | 'Edm.Int32'
  | 'Edm.Int64'
  | 'Edm.Boolean'
  | 'self.ResourcefulObjectType'
  | 'Collection(self.ResourcefulObjectType)'
  | 'Edm.String'

type ResourcefulOModelScalarType =
  | BasicResourcefulOModelScalarType
  | `Collection(${BasicResourcefulOModelScalarType})`

type ColumnAndComputedAccessorAttributes = {
  // shows in index operation results
  selectable: boolean
  // can be used for filtration
  filterable: boolean
  // can be used for sorting
  sortable: boolean
  // is visible in read operations
  readable: boolean
  // is usable in write operations
  insertable: boolean | 'required'
  // is usable in update operations
  updatable: boolean
  // is usable in bulk update operations
  bulkUpdatable: boolean
  // is a readonly immutable computed value
  isComputed: boolean
  // is a writable value which has a computed default value
  hasComputedDefault: boolean
}

// Utility to process knobs and ensure rule precedence
const processKnobs = (
  knobs: ColumnAndComputedAccessorAttributes
): ColumnAndComputedAccessorAttributes => {
  if (knobs.isComputed) {
    knobs.insertable = false
    knobs.updatable = false
    knobs.bulkUpdatable = false
  }
  if (knobs.hasComputedDefault) {
    if ('required' === knobs.insertable) {
      knobs.insertable = true
    }
  }
  if (knobs.readable === false) {
    knobs.selectable = false
    knobs.filterable = false
    knobs.sortable = false
  }
  if (knobs.selectable === false) {
    knobs.filterable = false
    knobs.sortable = false
  }
  if (knobs.updatable === false) {
    knobs.bulkUpdatable = false
  }
  return knobs
}

const isAutoincrementedPrimaryKey = (
  lucidDefinition: ModelColumnOptions,
  resourcefulDefinition: ResourcefulColumnDefinition<any>,
  propReadSchema: OpenAPIV3.SchemaObject | undefined,
  propWriteSchema: OpenAPIV3.SchemaObject | undefined
): boolean => {
  if (!lucidDefinition.isPrimary || !isNonArraySchemaObject(propWriteSchema)) {
    return false
  }
  if (
    !isInstanceOf<ResourcefulIntegerType>(
      resourcefulDefinition.type,
      'ResourcefulIntegerType',
      ResourcefulIntegerType
    ) &&
    !isInstanceOf<ResourcefulUnsignedIntegerType>(
      resourcefulDefinition.type,
      'ResourcefulUnsignedIntegerType',
      ResourcefulUnsignedIntegerType
    ) &&
    !isInstanceOf<ResourcefulBigintType>(
      resourcefulDefinition.type,
      'ResourcefulBigintType',
      ResourcefulBigintType
    )
  ) {
    return false
  }
  if (
    (isNonArraySchemaObject(propReadSchema) && propReadSchema.nullable) ||
    !isNonArraySchemaObject(propWriteSchema) ||
    (isNonArraySchemaObject(propReadSchema) && !propWriteSchema.nullable) ||
    propWriteSchema.writeOnly
  ) {
    return false
  }
  return true
}

type SimpleCapabilityAnnotationProperties = {
  [key: string]: boolean | number | string | Array<string> | SimpleCapabilityAnnotationProperties
}

const makeRecord = (
  key: string,
  value: boolean | number | string | Array<string> | SimpleCapabilityAnnotationProperties
) => {
  const ret: ElementCompact = {
    _attributes: {
      Property: key,
    },
  }
  switch (typeof value) {
    case 'boolean':
      ret._attributes!.Bool = value ? 'true' : 'false'
      break
    case 'number':
      ret._attributes!.Int = value
      break
    case 'string':
      ret._attributes!.String = value
      break
  }
  if (Array.isArray(value) && value.length > 0) {
    ret.Collection = {
      PropertyPath: value.map((v) => ({ _text: v })),
    }
  }
  if (isObject(value)) {
    ret.Record = {
      PropertyValue: Object.entries(value).map(([k, v]) => makeRecord(k, v)),
    }
  }
  return ret
}

const makeSimpleCapabilityAnnotation = (
  term: string,
  properties: SimpleCapabilityAnnotationProperties = {},
  attributes: ElementCompact['_attributes'] = {}
): ElementCompact => {
  const records = Object.entries(properties).map(([key, value]) => makeRecord(key, value))
  const ret: ElementCompact = {
    _attributes: {
      ...attributes,
      Term: term,
    },
    Record: records.length > 0 ? { PropertyValue: records } : undefined,
  }
  return stripUndefinedValuesFromObject(ret)
}

export const makeOpName = (...parts: string[]) => {
  const combinable: string[] = []
  parts.forEach((part) => {
    const frags = part.trim().split(/(\/|-|_)/gm)
    frags.forEach((frag) => {
      if (frag.includes(':uid')) {
        combinable.push('Instance')
      } else {
        combinable.push(frag)
      }
    })
  })
  return string.pascalCase(combinable.join(' '))
}

type OperationSpec = {
  kind: OpKind
  binding: Boundness // 'entity' => self.Model, 'collection' => Collection(self.Model)
  name: string // e.g., "SyncPosts", "FollowersCount"
  params?: ParamSpec[] // OData types e.g. Edm.Int32, self.Post, Collection(self.User)
  returnType?: string // required for functions, optional for actions
  entitySetPath?: string // e.g. "BindingParameter/posts" or "BindingParameter"
  isComposable?: boolean // functions only
  enabled?: (policy: ODataModelVerbPolicy) => boolean // policy gate
  annotations?: ElementCompact[] // optional per-op annotations
}

export class ODataMetadataService {
  readonly #opts: ResourcefulRouterOptionsService
  readonly #controllers: Map<string, ResourcefulModelController>
  readonly #models: Map<string, ResourcefulModel>
  readonly #policiesByModelName: Map<string, ODataModelVerbPolicy>
  readonly #modelMetaSchemas: Map<ResourcefulModel, ResourcefulModelMetaSchema>
  readonly #modelReadSchemas: Map<ResourcefulModel, ResourcefulModelOpenApiSchema>
  readonly #modelWriteSchemas: Map<ResourcefulModel, ResourcefulModelOpenApiSchema>
  readonly #policiesByModelCtor: Map<ResourcefulModel, ODataModelVerbPolicy>
  readonly #controllersByModelCtor: Map<ResourcefulModel, ResourcefulModelController>
  readonly #modelPathByModelCtor: Map<ResourcefulModel, string>

  constructor(
    opts: ResourcefulRouterOptionsService,
    controllers: Map<string, ResourcefulModelController>
  ) {
    this.#opts = opts
    this.#controllers = controllers
    this.#models = new Map<string, ResourcefulModel>()
    this.#policiesByModelName = new Map<string, ODataModelVerbPolicy>()
    this.#modelMetaSchemas = new Map<ResourcefulModel, ResourcefulModelMetaSchema>()
    this.#modelReadSchemas = new Map<ResourcefulModel, ResourcefulModelOpenApiSchema>()
    this.#modelWriteSchemas = new Map<ResourcefulModel, ResourcefulModelOpenApiSchema>()
    this.#policiesByModelCtor = new Map<ResourcefulModel, ODataModelVerbPolicy>()
    this.#controllersByModelCtor = new Map<ResourcefulModel, ResourcefulModelController>()
    this.#modelPathByModelCtor = new Map<ResourcefulModel, string>()
  }

  #currentModelCan(verb: ResourcefulRouterVerb, controller?: ResourcefulModelController): boolean {
    if (!controller) {
      return !this.#opts.except.includes(verb)
    }
    // Defensive: controller.options should always be present, but guard to prevent hard crash
    const controllerExcept = controller?.options?.except || []
    return !this.#opts.except.includes(verb) && !controllerExcept.includes(verb as any)
  }

  async #cacheModel(
    ctx: HttpContext,
    app: AppServiceInstance,
    model: ResourcefulModel,
    onRelatedModel: (related: ResourcefulModel) => void
  ) {
    const modelName = model.$resourcefulName
    if (!this.#models.has(modelName)) {
      // we have not registered this model yet
      this.#models.set(modelName, model)
    }
    if (!this.#modelMetaSchemas.has(model)) {
      const asResourceful = await model.$getAsResourcefulForContext(ctx, app)
      this.#modelMetaSchemas.set(model, asResourceful)
    }
    if (!this.#modelReadSchemas.has(model) && !this.#modelWriteSchemas.has(model)) {
      const [readSchema, writeSchema] = await Promise.all([
        model.$asOpenApiSchemaObject(ctx, app, 'read'),
        model.$asOpenApiSchemaObject(ctx, app, 'write'),
      ])
      this.#modelReadSchemas.set(model, readSchema)
      this.#modelWriteSchemas.set(model, writeSchema)
    } else if (!this.#modelReadSchemas.has(model)) {
      const readSchema = await model.$asOpenApiSchemaObject(ctx, app, 'read')
      this.#modelReadSchemas.set(model, readSchema)
    } else if (!this.#modelWriteSchemas.has(model)) {
      const writeSchema = await model.$asOpenApiSchemaObject(ctx, app, 'write')
      this.#modelWriteSchemas.set(model, writeSchema)
    }
    await pMap(
      Array.from(model.$relationsDefinitions.values()),
      async (relation) => {
        relation.boot()
        const related = relation.relatedModel()
        if (isResourcefulModel(related)) {
          onRelatedModel(related)
        }
      },
      { concurrency: 10 }
    )
  }

  async #boot(ctx: HttpContext) {
    const relatedModelsToProcess = new Map<string, ResourcefulModel>()
    await pMap(
      Array.from(this.#controllers.entries()),
      async ([modelPath, controller]) => {
        const { model, app } = await controller.resolveBaseRequirements()
        if (!isResourcefulModel(model)) {
          return
        }
        const policy: ODataModelVerbPolicy = {
          index: this.#currentModelCan('index', controller),
          create: this.#currentModelCan('create', controller),
          read: this.#currentModelCan('read', controller),
          readRelated: this.#currentModelCan('readRelated', controller),
          update: this.#currentModelCan('update', controller),
          bulkUpdate: this.#currentModelCan('bulkUpdate', controller),
          syncRelated: this.#currentModelCan('syncRelated', controller),
          delete: this.#currentModelCan('delete', controller),
        }
        this.#policiesByModelName.set(model.$resourcefulName, policy)
        this.#policiesByModelCtor.set(model, policy)
        this.#controllersByModelCtor.set(model, controller)
        this.#modelPathByModelCtor.set(model, modelPath)
        await this.#cacheModel(ctx, app, model, (related) => {
          const relatedModelName = related.$resourcefulName
          if (
            !this.#models.has(relatedModelName) &&
            !relatedModelsToProcess.has(relatedModelName)
          ) {
            relatedModelsToProcess.set(relatedModelName, related)
          }
        })
      },
      { concurrency: 5 }
    )
    while (relatedModelsToProcess.size > 0) {
      const modelsToProcess = Array.from(relatedModelsToProcess.values())
      relatedModelsToProcess.clear()
      await pMap(
        modelsToProcess,
        async (model) => {
          const { default: app } = await import('@adonisjs/core/services/app')
          await this.#cacheModel(ctx, app, model, (related) => {
            const relatedModelName = related.$resourcefulName
            if (
              !this.#models.has(relatedModelName) &&
              !relatedModelsToProcess.has(relatedModelName)
            ) {
              relatedModelsToProcess.set(relatedModelName, related)
            }
          })
        },
        { concurrency: 5 }
      )
    }
  }

  async generateMetadataElements(ctx: HttpContext): Promise<ElementCompact> {
    await this.#boot(ctx)
    const ret: ElementCompact = {
      'edmx:Edmx': {
        '_attributes': {
          'Version': '4.0',
          'xmlns:edmx': 'http://docs.oasis-open.org/odata/ns/edmx',
        },
        'edmx:Reference': [
          {
            '_attributes': {
              Uri: 'https://docs.oasis-open.org/odata/odata-vocabularies/v4.0/Org.OData.Validation.V1.xml',
            },
            'edmx:Include': {
              _attributes: {
                Namespace: 'Org.OData.Validation.V1',
                Alias: 'Validation',
              },
            },
          },
          {
            '_attributes': {
              Uri: 'https://docs.oasis-open.org/odata/odata-vocabularies/v4.0/Org.OData.Core.V1.xml',
            },
            'edmx:Include': {
              _attributes: {
                Namespace: 'Org.OData.Core.V1',
                Alias: 'Core',
              },
            },
          },
          {
            '_attributes': {
              Uri: 'https://docs.oasis-open.org/odata/odata-vocabularies/v4.0/Org.OData.Capabilities.V1.xml',
            },
            'edmx:Include': {
              _attributes: {
                Namespace: 'Org.OData.Capabilities.V1',
                Alias: 'Capabilities',
              },
            },
          },
        ],
        'edmx:DataServices': {
          Schema: {
            _attributes: {
              Namespace: 'Default',
              Alias: 'self',
              xmlns: 'http://docs.oasis-open.org/odata/ns/edm',
            },
            ...(await this.generateEdmElements()),
          },
        },
      },
    }
    return ret
  }

  #getEdmIntScalarType(type: ResourcefulIntegerType | ResourcefulUnsignedIntegerType) {
    let min: number = Number.MIN_SAFE_INTEGER
    if (
      isInstanceOf<ResourcefulUnsignedIntegerType>(
        type,
        'ResourcefulUnsignedIntegerType',
        ResourcefulUnsignedIntegerType
      )
    ) {
      min = 0
    } else {
      min = type.exclusiveMinimum ? type.minimum + 1 : type.minimum
    }
    const max = type.exclusiveMaximum
      ? type.maximum
      : typeof type.maximum === 'bigint'
        ? type.maximum - BigInt(1)
        : type.maximum - 1
    return min < -2_147_483_648 || max > 2_147_483_647 ? 'Edm.Int64' : 'Edm.Int32'
  }

  #resourcefulDataTypeToEdmScalarType(type: ResourcefulDataType): ResourcefulOModelScalarType {
    switch (true) {
      case isInstanceOf<ResourcefulStringType>(
        type,
        'ResourcefulStringType',
        ResourcefulStringType
      ):
        return 'Edm.String'
      case isInstanceOf<ResourcefulDateType>(type, 'ResourcefulDateType', ResourcefulDateType):
        return 'Edm.Date'
      case isInstanceOf<ResourcefulDateTimeType>(
        type,
        'ResourcefulDateTimeType',
        ResourcefulDateTimeType
      ):
        return 'Edm.DateTimeOffset'
      case isInstanceOf<ResourcefulBinaryType>(
        type,
        'ResourcefulBinaryType',
        ResourcefulBinaryType
      ):
        return 'Edm.Binary'
      case isInstanceOf<ResourcefulNumberType>(
        type,
        'ResourcefulNumberType',
        ResourcefulNumberType
      ):
        return 'Edm.Decimal'
      case isInstanceOf<ResourcefulBigintType>(
        type,
        'ResourcefulBigintType',
        ResourcefulBigintType
      ):
        return 'Edm.Int64'
      case isInstanceOf<ResourcefulIntegerType>(
        type,
        'ResourcefulIntegerType',
        ResourcefulIntegerType
      ):
      case isInstanceOf<ResourcefulUnsignedIntegerType>(
        type,
        'ResourcefulUnsignedIntegerType',
        ResourcefulUnsignedIntegerType
      ):
        return this.#getEdmIntScalarType(type)
      case isInstanceOf<ResourcefulBooleanType>(
        type,
        'ResourcefulBooleanType',
        ResourcefulBooleanType
      ):
        return 'Edm.Boolean'
      case isInstanceOf(type, 'ResourcefulObjectType', ResourcefulObjectType):
        return 'self.ResourcefulObjectType'
      case isInstanceOf<ResourcefulArrayType>(type, 'ResourcefulArrayType', ResourcefulArrayType): {
        const t: ResourcefulArrayType = type
        if ('oneOf' in t.items || 'allOf' in t.items || 'anyOf' in t.items || 'not' in t.items) {
          return 'Collection(self.ResourcefulObjectType)'
        }
        const it = this.#resourcefulDataTypeToEdmScalarType(
          t.items as unknown as ResourcefulDataType
        )
        return `Collection(${it})` as `Collection(${BasicResourcefulOModelScalarType})`
      }
      default:
        return 'Edm.String'
    }
  }

  #getEdmScalarType(
    resourcefulDef: ResourcefulColumnDefinition<any> | ResourcefulComputedAccessorDefinition<any>
  ) {
    return this.#resourcefulDataTypeToEdmScalarType(resourcefulDef.type)
  }

  #getValidationAnnotationsForProperty(
    scalarType: string,
    schema: OpenAPIV3.SchemaObject | undefined
  ): AnnotationAttributes[] {
    const ret: AnnotationAttributes[] = []
    if (isObject(schema) && 'array' !== schema.type) {
      const s: OpenAPIV3.NonArraySchemaObject = schema as OpenAPIV3.NonArraySchemaObject
      const valueKey = scalarType
        .replace(/^Edm\./, '')
        .replace(/32$/, '')
        .replace(/64$/, '') as Extract<keyof AnnotationAttributes, string>
      if ('Edm.String' === scalarType && s.pattern) {
        ret.push({
          Term: 'Validation.Pattern',
          String: s.pattern,
        })
      }
      if ('undefined' !== typeof s.minimum) {
        ret.push({
          Term: 'Validation.Minimum',
          [valueKey]: s.minimum.toString(),
          Exclusive: s.exclusiveMinimum ? 'true' : undefined,
        } as AnnotationAttributes)
      }
      if ('undefined' !== typeof s.maximum) {
        ret.push({
          Term: 'Validation.Maximum',
          [valueKey]: s.maximum.toString(),
          Exclusive: s.exclusiveMaximum ? 'true' : undefined,
        } as AnnotationAttributes)
      }
      if ('undefined' !== typeof s.multipleOf) {
        ret.push({
          Term: 'Validation.MultipleOf',
          [valueKey]: s.multipleOf.toString(),
        } as AnnotationAttributes)
      }
    }
    return ret
  }

  #resolveSchemaPropertySchemas(
    props: { [key: string]: OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject } = {},
    mode: 'read' | 'write' = 'read',
    depth: number = 0
  ): Map<string, OpenAPIV3.SchemaObject> {
    const ret = new Map<string, OpenAPIV3.SchemaObject>()
    if (depth > 1) {
      return ret
    }
    Object.entries(props).forEach(([propName, propSchema]) => {
      if ('$ref' in propSchema) {
        const referencedModelName = propSchema.$ref.split('/').pop() || 'Unknown'
        const referencedModel = this.#models.get(referencedModelName)
        // if we cannot find the referenced model, skip it
        if (!referencedModel) return
        const referencedModelSchema =
          'read' === mode
            ? this.#modelReadSchemas.get(referencedModel)
            : this.#modelWriteSchemas.get(referencedModel)
        // if we cannot find the referenced model schema, skip it
        if (!referencedModelSchema) return
        const resolvedReferencedModelPropertySchemas = this.#resolveSchemaPropertySchemas(
          referencedModelSchema.properties,
          mode,
          depth + 1
        )
        const resolvedReferenceModelPropertySchemasObject: {
          [key: string]: OpenAPIV3.SchemaObject
        } = Object.assign(
          {},
          ...Array.from(resolvedReferencedModelPropertySchemas.entries()).map(([k, v]) => ({
            [k]: v,
          }))
        )
        ret.set(propName, {
          ...referencedModelSchema,
          properties: resolvedReferenceModelPropertySchemasObject,
        })
      } else {
        ret.set(propName, propSchema)
      }
    })
    return ret
  }

  #hasComputedDefault(
    resourcefulDef: ResourcefulColumnDefinition<any> | ResourcefulComputedAccessorDefinition<any>,
    lucidDef: ModelColumnOptions
  ) {
    if (
      !isInstanceOf<ResourcefulDateType>(
        resourcefulDef.type,
        'ResourcefulDateType',
        ResourcefulDateType
      ) &&
      !isInstanceOf<ResourcefulDateTimeType>(
        resourcefulDef.type,
        'ResourcefulDateTimeType',
        ResourcefulDateTimeType
      )
    ) {
      return false
    }
    const dateColumnOptions: DateColumnOptions = lucidDef as unknown as DateColumnOptions
    return true === dateColumnOptions.autoCreate
  }

  async generateEdmElements(): Promise<ElementCompact> {
    const ret: ElementCompact = {}
    const EntityTypeMap = new Map<string, ElementCompact>()
    const ComplexTypeMap = new Map<string, ElementCompact>()
    const EnumTypeMap = new Map<string, ElementCompact>()
    const EntitySetMap = new Map<string, ElementCompact>()
    const AnnotationsArray: Array<ElementCompact> = [
      {
        _attributes: { Target: 'self.Container' },
        Annotation: [
          {
            _attributes: { Term: 'Capabilities.BatchSupport' },
            Record: {
              PropertyValue: [
                {
                  _attributes: {
                    Property: 'Supported',
                    Bool: 'false',
                  },
                },
              ],
            },
          },
        ],
      },
    ]
    const ActionMap = new Map<string, ElementCompact[]>()
    const FunctionMap = new Map<string, ElementCompact[]>()
    const ActionImportSet = new Set<string>()
    const FunctionImportSet = new Set<string>()
    // Registry of actions, functions and operations by model
    const modelOps = new Map<ResourcefulModel, OperationSpec[]>()
    const registerModelOps = (modelCtor: ResourcefulModel, ops: OperationSpec[]) => {
      const cur = modelOps.get(modelCtor) || []
      modelOps.set(modelCtor, cur.concat(ops))
    }
    let hasResourcefulObjectType = false
    this.#models.forEach((model, modelName) => {
      const controller = this.#controllersByModelCtor.get(model)
      const readSchema = this.#modelReadSchemas.get(model)!
      const writeSchema = this.#modelWriteSchemas.get(model)!
      const filterable: string[] = isObject(readSchema['x-resourceful-filterable'])
        ? Object.keys(readSchema['x-resourceful-filterable'])
        : []
      const sortable: string[] = readSchema['x-resourceful-sortable'] || []
      const readablePropertySchemas = this.#resolveSchemaPropertySchemas(
        readSchema.properties,
        'read'
      )
      const writablePropertySchemas = this.#resolveSchemaPropertySchemas(
        writeSchema.properties,
        'write'
      )
      const modelPolicy = this.#policiesByModelCtor.get(model) || {
        index: false,
        create: false,
        read: false,
        readRelated: false,
        update: false,
        bulkUpdate: false,
        syncRelated: false,
        delete: false,
      }
      const entityTypeName = model.$resourcefulName
      const rawEntitySetName = this.#modelPathByModelCtor.get(model)
      const entitySetName = rawEntitySetName || modelName
      const resourcefulColumns = Array.from(model.$resourcefulColumns.entries())
      const resourcefulComputedAccessors = Array.from(model.$resourcefulComputedAccessors.entries())
      const resourcefulRelationships = Array.from(model.$resourcefulRelationships.entries())
      // sort all of the information into maps for easy access
      const modelEntitySetNavigationPropertyBindings: NavigationPropertyBindingAttributes[] = []
      const modelEntitySetAnnotations: ElementCompact[] = []
      const modelPropertyMap = new Map<string, PropertyAttributes>()
      const modelPropertyAnnotationsMap = new Map<string, AnnotationAttributes[]>()
      const modelPropertyAnnotationsCompactElementMap = new Map<string, ElementCompact[]>()
      const modelNavigationPropertyMap = new Map<string, NavigationPropertyAttributes>()
      const modelNavigationPropertyReferentialConstraintsMap = new Map<string, ElementCompact[]>()
      const modelNavigationPropertyAnnotationsMap = new Map<string, AnnotationAttributes[]>()
      const modelNavigationPropertyAnnotationsCompactElementMap = new Map<
        string,
        ElementCompact[]
      >()
      const insertRequiredProperties = new Set<string>()
      const nonInsertableProperties = new Set<string>()
      const nonUpdatableProperties = new Set<string>()
      const nonBulkUpdatableProperties = new Set<string>()
      const nonSelectableProperties = new Set<string>()
      const nonReadableProperties = new Set<string>()
      const nonFilterableProperties = new Set<string>()
      const nonSortableProperties = new Set<string>()
      const nonNavigableProperties = new Set<string>()
      const nonExpandableProperties = new Set<string>()
      const modelOperationSpecMap: Map<string, OperationSpec> = new Map()
      const modelOperationRestrictions: ElementCompact[] = []
      // iterate through columns, computed accessors and then relationships
      // to populate the buckets appropriately
      resourcefulColumns.forEach(([propName, resourcefulDef]) => {
        const lucidDefinition = model.$columnsDefinitions.get(propName)!
        // Columns with serializeAs: null are intentionally excluded from
        // serialized output (toJSON). Declaring them in $metadata would cause
        // OData clients (Power BI, Excel) to expect data that is never
        // provided, producing per-cell "Error" values. Skip them so the
        // advertised schema matches the wire format.
        if (lucidDefinition.serializeAs === null) return
        const propReadSchema = readablePropertySchemas.get(propName)
        const propWriteSchema = writablePropertySchemas.get(propName)
        const Type = this.#getEdmScalarType(resourcefulDef)
        if (Type.includes('self.ResourcefulObjectType')) {
          hasResourcefulObjectType = true
        }
        const propPropertyAttributes: PropertyAttributes = {
          Name: propName,
          Type,
          Nullable: true === resourcefulDef.nullable ? undefined : false,
        }
        const propAnnotationAttributes: AnnotationAttributes[] = []
        const propAnnotationElements: ElementCompact[] = []
        this.#getValidationAnnotationsForProperty(Type, propReadSchema).forEach((a) =>
          propAnnotationAttributes.push(a)
        )
        if (isNonArraySchemaObject(propReadSchema) && Array.isArray(propReadSchema.enum)) {
          propAnnotationElements.push({
            _attributes: {
              Term: 'Validation.AllowedValues',
            },
            Collection: {
              AllowedValue: propReadSchema.enum.map((enumValue) => ({
                _attributes: {
                  Value: String(isObject(enumValue) ? enumValue.value : enumValue),
                },
              })),
            },
          })
        }
        const knobs = processKnobs({
          selectable: modelPolicy.index && isObject(propReadSchema),
          filterable:
            modelPolicy.index && isObject(propReadSchema) && filterable.includes(propName),
          sortable: modelPolicy.index && isObject(propReadSchema) && sortable.includes(propName),
          readable: modelPolicy.read && isObject(propReadSchema),
          insertable:
            modelPolicy.create &&
            isObject(propWriteSchema) &&
            !isAutoincrementedPrimaryKey(
              lucidDefinition,
              resourcefulDef,
              propReadSchema,
              propWriteSchema
            )
              ? writeSchema.required?.includes(propName)
                ? 'required'
                : true
              : false,
          updatable: modelPolicy.update && isObject(propWriteSchema),
          bulkUpdatable: modelPolicy.bulkUpdate && isObject(propWriteSchema),
          isComputed: false,
          hasComputedDefault: this.#hasComputedDefault(resourcefulDef, lucidDefinition),
        } satisfies ColumnAndComputedAccessorAttributes)
        if (false === knobs.selectable) {
          nonSelectableProperties.add(propName)
        }
        if (false === knobs.filterable) {
          nonFilterableProperties.add(propName)
        }
        if (false === knobs.sortable) {
          nonSortableProperties.add(propName)
        }
        if (false === knobs.readable) {
          nonReadableProperties.add(propName)
        }
        if (false === knobs.insertable) {
          nonInsertableProperties.add(propName)
        } else if ('required' === knobs.insertable) {
          insertRequiredProperties.add(propName)
        }
        if (false === knobs.updatable) {
          nonUpdatableProperties.add(propName)
        }
        if (false === knobs.bulkUpdatable) {
          nonBulkUpdatableProperties.add(propName)
        }
        if (knobs.isComputed) {
          propAnnotationAttributes.push({
            Term: 'Core.Computed',
            Bool: true,
          })
        }
        if (knobs.hasComputedDefault) {
          propAnnotationAttributes.push({
            Term: 'Core.ComputedDefaultValue',
            Bool: true,
          })
        }
        if (knobs.readable && !knobs.insertable && !knobs.updatable) {
          propAnnotationAttributes.push({
            Term: 'Core.Permissions',
            EnumMember: 'Core.Permission/Read',
          })
        }
        if (!knobs.readable && knobs.insertable && knobs.updatable) {
          propAnnotationAttributes.push({
            Term: 'Core.Permissions',
            EnumMember: 'Core.Permission/Write',
          })
        }
        modelPropertyMap.set(propName, propPropertyAttributes)
        if (propAnnotationAttributes.length > 0) {
          modelPropertyAnnotationsMap.set(propName, propAnnotationAttributes)
        }
        if (propAnnotationElements.length > 0) {
          modelPropertyAnnotationsCompactElementMap.set(propName, propAnnotationElements)
        }
      })
      resourcefulComputedAccessors.forEach(([propName, resourcefulDef]) => {
        const propReadSchema = readablePropertySchemas.get(propName)
        const propWriteSchema = writablePropertySchemas.get(propName)
        const Type = this.#getEdmScalarType(resourcefulDef)
        if (Type.includes('self.ResourcefulObjectType')) {
          hasResourcefulObjectType = true
        }
        const propPropertyAttributes: PropertyAttributes = {
          Name: propName,
          Type,
          Nullable: true === resourcefulDef.nullable ? undefined : false,
        }
        const propAnnotationAttributes: AnnotationAttributes[] = []
        const propAnnotationElements: ElementCompact[] = []
        this.#getValidationAnnotationsForProperty(Type, propReadSchema).forEach((a) =>
          propAnnotationAttributes.push(a)
        )
        if (isNonArraySchemaObject(propReadSchema) && Array.isArray(propReadSchema.enum)) {
          propAnnotationElements.push({
            _attributes: {
              Term: 'Validation.AllowedValues',
            },
            Collection: {
              AllowedValue: propReadSchema.enum.map((enumValue) => ({
                _attributes: {
                  Value: String(isObject(enumValue) ? enumValue.value : enumValue),
                },
              })),
            },
          })
        }
        const knobs = processKnobs({
          selectable: modelPolicy.index && isObject(propReadSchema),
          filterable:
            modelPolicy.index && isObject(propReadSchema) && filterable.includes(propName),
          sortable: modelPolicy.index && isObject(propReadSchema) && sortable.includes(propName),
          readable: modelPolicy.read && isObject(propReadSchema),
          insertable:
            modelPolicy.create && resourcefulDef.writable && isObject(propWriteSchema)
              ? writeSchema.required?.includes(propName)
                ? 'required'
                : true
              : false,
          updatable: modelPolicy.update && resourcefulDef.writable && isObject(propWriteSchema),
          bulkUpdatable: false,
          isComputed: !resourcefulDef.writable,
          hasComputedDefault: false,
        } satisfies ColumnAndComputedAccessorAttributes)
        if (false === knobs.selectable) {
          nonSelectableProperties.add(propName)
        }
        if (false === knobs.filterable) {
          nonFilterableProperties.add(propName)
        }
        if (false === knobs.sortable) {
          nonSortableProperties.add(propName)
        }
        if (false === knobs.readable) {
          nonReadableProperties.add(propName)
        }
        if (false === knobs.insertable) {
          nonInsertableProperties.add(propName)
        } else if ('required' === knobs.insertable) {
          insertRequiredProperties.add(propName)
        }
        if (false === knobs.updatable) {
          nonUpdatableProperties.add(propName)
        }
        if (false === knobs.bulkUpdatable) {
          nonBulkUpdatableProperties.add(propName)
        }
        if (knobs.isComputed) {
          propAnnotationAttributes.push({
            Term: 'Core.Computed',
            Bool: true,
          })
        }
        if (knobs.hasComputedDefault) {
          propAnnotationAttributes.push({
            Term: 'Core.ComputedDefaultValue',
            Bool: true,
          })
        }
        if (knobs.readable && !knobs.insertable && !knobs.updatable) {
          propAnnotationAttributes.push({
            Term: 'Core.Permissions',
            EnumMember: 'Core.Permission/Read',
          })
        }
        if (!knobs.readable && knobs.insertable && knobs.updatable) {
          propAnnotationAttributes.push({
            Term: 'Core.Permissions',
            EnumMember: 'Core.Permission/Write',
          })
        }
        modelPropertyMap.set(propName, propPropertyAttributes)
        if (propAnnotationAttributes.length > 0) {
          modelPropertyAnnotationsMap.set(propName, propAnnotationAttributes)
        }
        if (propAnnotationElements.length > 0) {
          modelPropertyAnnotationsCompactElementMap.set(propName, propAnnotationElements)
        }
      })
      const addModelEntitySetNavigationPropertyBindings = (
        propName: string,
        relatedModel: ResourcefulModel
      ) => {
        const modelPath = this.#modelPathByModelCtor.get(relatedModel)
        if (!modelPath) {
          return
        }
        const fixedModelPath = modelPath
        modelEntitySetNavigationPropertyBindings.push({
          Path: propName,
          Target: fixedModelPath,
        })
      }
      const processNonCollectionRelationship = (
        propName: string,
        lucidDef:
          | HasOneRelationContract<LucidModel, LucidModel>
          | BelongsToRelationContract<LucidModel, LucidModel>,
        propReadSchema: OpenAPIV3.SchemaObject | undefined
      ) => {
        if (!isNonArraySchemaObject(propReadSchema) || (!modelPolicy.index && !modelPolicy.read))
          return
        const relatedModel = lucidDef.relatedModel()
        if (!isResourcefulModel(relatedModel)) {
          return
        }
        addModelEntitySetNavigationPropertyBindings(propName, relatedModel)
        const targetType = relatedModel.$resourcefulName
        const fkProp = lucidDef.foreignKey
        const fkCol =
          'belongsTo' === lucidDef.type
            ? model.$resourcefulColumns.get(fkProp)
            : relatedModel.$resourcefulColumns.get(fkProp)
        const nullable = fkCol ? true === fkCol.nullable : true
        const propNavigationPropertyAttributes: NavigationPropertyAttributes = {
          Name: propName,
          Type: `self.${targetType}`,
          Nullable: nullable,
        }
        const propNavigationPropertyReferentialConstraints: ElementCompact[] = []
        if (lucidDef.type === 'belongsTo') {
          const referentialConstraint = {
            _attributes: {
              Property: lucidDef.foreignKey,
              ReferencedProperty: lucidDef.localKey,
            },
          }
          propNavigationPropertyReferentialConstraints.push(referentialConstraint)
        }
        const propNavigationPropertyAnnotations: AnnotationAttributes[] = []
        propNavigationPropertyAnnotations.push({
          Term: 'Core.AutoExpand',
          Bool: true,
        })
        modelNavigationPropertyMap.set(propName, propNavigationPropertyAttributes)
        if (propNavigationPropertyReferentialConstraints.length > 0) {
          modelNavigationPropertyReferentialConstraintsMap.set(
            propName,
            propNavigationPropertyReferentialConstraints
          )
        }
        if (propNavigationPropertyAnnotations.length > 0) {
          modelNavigationPropertyAnnotationsMap.set(propName, propNavigationPropertyAnnotations)
        }
        nonNavigableProperties.add(propName)
        nonExpandableProperties.add(propName)
      }
      const processCollectionRelationship = (
        propName: string,
        lucidDef:
          | HasManyRelationContract<LucidModel, LucidModel>
          | HasManyThroughRelationContract<LucidModel, LucidModel>
          | ManyToManyRelationContract<LucidModel, LucidModel>
      ) => {
        const relatedModel = lucidDef.relatedModel()
        if (!isResourcefulModel(relatedModel)) {
          return
        }
        addModelEntitySetNavigationPropertyBindings(propName, relatedModel)
        const targetType = relatedModel.$resourcefulName
        const propNavigationPropertyAttributes: NavigationPropertyAttributes = {
          Name: propName,
          Type: `Collection(self.${targetType})`,
        }
        modelNavigationPropertyMap.set(propName, propNavigationPropertyAttributes)
        if (!modelPolicy.readRelated) {
          nonNavigableProperties.add(propName)
        }
        nonExpandableProperties.add(propName)
        if (modelPolicy.syncRelated) {
          const opName = string.pascalCase(
            ['sync', model.$resourcefulName, 'related', propName].join(' ')
          )
          if (!modelOperationSpecMap.has(opName)) {
            const opSpec: OperationSpec = {
              kind: 'action',
              binding: 'entity',
              name: opName,
              params: [{ name: 'ruids', type: 'Collection(Edm.String)', nullable: false }],
              returnType: 'Edm.String',
              entitySetPath: propName,
              isComposable: false,
              enabled: () => true,
            }
            modelOperationSpecMap.set(opName, opSpec)
            modelOperationRestrictions.push({
              PropertyValue: [
                {
                  _attributes: {
                    Property: 'Operation',
                    String: `Default.${opName}`,
                  },
                },
                {
                  _attributes: {
                    Property: 'Target',
                    NavigationPropertyPath: propName,
                  },
                },
              ],
            })
          }
        }
      }
      resourcefulRelationships.forEach(([propName]) => {
        const lucidDefinition = model.$relationsDefinitions.get(propName)!
        const propReadSchema = readablePropertySchemas.get(propName)
        // differentiate between collection and non collection relationships
        if (['hasMany', 'manyToMany', 'hasManyThrough'].includes(lucidDefinition.type)) {
          // process as collection
          processCollectionRelationship(
            propName,
            lucidDefinition as
              | HasManyRelationContract<LucidModel, LucidModel>
              | HasManyThroughRelationContract<LucidModel, LucidModel>
              | ManyToManyRelationContract<LucidModel, LucidModel>
          )
        } else {
          // process as single entity
          processNonCollectionRelationship(
            propName,
            lucidDefinition as
              | HasOneRelationContract<LucidModel, LucidModel>
              | BelongsToRelationContract<LucidModel, LucidModel>,
            propReadSchema
          )
        }
      })
      // create the entity type now that we have all of the information
      const modelEntityType: ElementCompact = stripUndefinedValuesFromObject({
        _attributes: {
          Name: entityTypeName,
        } satisfies EntityTypeAttributes,
        Key: {
          PropertyRef: {
            _attributes: { Name: model.primaryKey },
          },
        },
        Property:
          modelPropertyMap.size > 0
            ? Array.from(modelPropertyMap.entries()).map(([prop, attrs]) => {
                const propAnnotations = modelPropertyAnnotationsMap.get(prop) || []
                const propAnnotationElements =
                  modelPropertyAnnotationsCompactElementMap.get(prop) || []
                const Annotation = [
                  ...propAnnotations.map((a) => ({ _attributes: a })),
                  ...propAnnotationElements,
                ]
                return stripUndefinedValuesFromObject({
                  _attributes: attrs,
                  Annotation: Annotation.length > 0 ? Annotation : undefined,
                })
              })
            : undefined,
        NavigationProperty:
          modelNavigationPropertyMap.size > 0
            ? Array.from(modelNavigationPropertyMap.entries()).map(([prop, attrs]) => {
                const propReferentialConstraints =
                  modelNavigationPropertyReferentialConstraintsMap.get(prop) || []
                const propAnnotations = modelNavigationPropertyAnnotationsMap.get(prop) || []
                const propAnnotationElements =
                  modelNavigationPropertyAnnotationsCompactElementMap.get(prop) || []
                const Annotation = [
                  ...propAnnotations.map((a) => ({ _attributes: a })),
                  ...propAnnotationElements,
                ]
                return stripUndefinedValuesFromObject({
                  _attributes: attrs,
                  ReferentialConstraint:
                    propReferentialConstraints.length > 0 ? propReferentialConstraints : undefined,
                  Annotation: Annotation.length > 0 ? Annotation : undefined,
                })
              })
            : undefined,
      })
      EntityTypeMap.set(modelName, modelEntityType)
      // create the entity set now that we have all of the information
      const modelEntitySet: ElementCompact = stripUndefinedValuesFromObject({
        _attributes: {
          Name: entitySetName,
          EntityType: `self.${entityTypeName}`,
        } satisfies EntitySetAttributes,
        NavigationPropertyBinding:
          modelEntitySetNavigationPropertyBindings.length === 0
            ? undefined
            : modelEntitySetNavigationPropertyBindings.map((b) => ({ _attributes: b })),
      })
      EntitySetMap.set(entitySetName, modelEntitySet)
      // build the model-level annotations now that we have all of the information
      const annotationsTarget = `self.Container/${entitySetName}`
      if (modelPolicy.index) {
        modelEntitySetAnnotations.push(
          makeSimpleCapabilityAnnotation('Capabilities.SearchRestrictions', { Searchable: true })
        )
        modelEntitySetAnnotations.push(
          makeSimpleCapabilityAnnotation(
            'Capabilities.FilterRestrictions',
            stripUndefinedValuesFromObject({
              Filterable: true,
              NonFilterableProperties:
                nonFilterableProperties.size > 0 ? Array.from(nonFilterableProperties) : undefined,
            } as SimpleCapabilityAnnotationProperties)
          )
        )
        modelEntitySetAnnotations.push(
          makeSimpleCapabilityAnnotation(
            'Capabilities.SortRestrictions',
            stripUndefinedValuesFromObject({
              Sortable: true,
              NonSortableProperties:
                nonSortableProperties.size > 0 ? Array.from(nonSortableProperties) : undefined,
            } as SimpleCapabilityAnnotationProperties)
          )
        )
        modelEntitySetAnnotations.push(
          makeSimpleCapabilityAnnotation('Capabilities.CountRestrictions', {
            Countable: true,
          })
        )
        modelEntitySetAnnotations.push(
          makeSimpleCapabilityAnnotation(
            'Capabilities.TopSupported',
            {},
            {
              Bool: 'true',
            }
          )
        )
        modelEntitySetAnnotations.push(
          makeSimpleCapabilityAnnotation(
            'Capabilities.SkipSupported',
            {},
            {
              Bool: 'true',
            }
          )
        )
        modelEntitySetAnnotations.push(
          makeSimpleCapabilityAnnotation(
            'Capabilities.SelectSupport',
            stripUndefinedValuesFromObject({
              Supported: true,
              NonSelectableProperties:
                nonSelectableProperties.size > 0 ? Array.from(nonSelectableProperties) : undefined,
            } as SimpleCapabilityAnnotationProperties)
          )
        )
      } else {
        modelEntitySetAnnotations.push(
          makeSimpleCapabilityAnnotation('Capabilities.SearchRestrictions', { Searchable: false })
        )
        modelEntitySetAnnotations.push(
          makeSimpleCapabilityAnnotation('Capabilities.FilterRestrictions', { Filterable: false })
        )
        modelEntitySetAnnotations.push(
          makeSimpleCapabilityAnnotation('Capabilities.SortRestrictions', { Sortable: false })
        )
        modelEntitySetAnnotations.push(
          makeSimpleCapabilityAnnotation('Capabilities.CountRestrictions', {
            Countable: false,
          })
        )
        modelEntitySetAnnotations.push(
          makeSimpleCapabilityAnnotation(
            'Capabilities.TopSupported',
            {},
            {
              Bool: 'false',
            }
          )
        )
        modelEntitySetAnnotations.push(
          makeSimpleCapabilityAnnotation(
            'Capabilities.SkipSupported',
            {},
            {
              Bool: 'false',
            }
          )
        )
        modelEntitySetAnnotations.push(
          makeSimpleCapabilityAnnotation('Capabilities.SelectSupport', {
            Supported: false,
          })
        )
      }
      if (modelPolicy.index || modelPolicy.read) {
        modelEntitySetAnnotations.push(
          makeSimpleCapabilityAnnotation('Capabilities.ReadRestrictions', {
            Readable: true,
            ReadByKeyRestrictions: {
              Readable: modelPolicy.read,
            },
          })
        )
        if (modelNavigationPropertyMap.size > 0) {
          if (modelNavigationPropertyMap.size !== nonExpandableProperties.size) {
            modelEntitySetAnnotations.push({
              _attributes: {
                Term: 'Capabilities.ExpandRestrictions',
              },
              Record: {
                PropertyValue: [
                  { _attributes: { Property: 'Expandable', Bool: 'true' } },
                  { _attributes: { Property: 'MaxLevels', Int: '1' } },
                  nonExpandableProperties.size > 0
                    ? {
                        _attributes: { Property: 'NonExpandableProperties' },
                        Collection: {
                          NavigationPropertyPath: Array.from(nonExpandableProperties).map((v) => ({
                            _text: v,
                          })),
                        },
                      }
                    : undefined,
                ].filter(isObject),
              },
            })
            if (nonNavigableProperties.size > 0) {
              modelEntitySetAnnotations.push({
                _attributes: {
                  Term: 'Capabilities.NavigationRestrictions',
                },
                Record: {
                  PropertyValue: {
                    _attributes: { Property: 'RestrictedProperties' },
                    Collection: {
                      Record: Array.from(nonNavigableProperties).map((prop) => ({
                        PropertyValue: [
                          {
                            _attributes: {
                              Property: 'NavigationProperty',
                              NavigationPropertyPath: prop,
                            },
                          },
                          {
                            _attributes: {
                              Property: 'Navigability',
                              EnumMember: 'Capabilities.NavigationType/None',
                            },
                          },
                        ],
                      })),
                    },
                  },
                },
              })
            }
          } else {
            modelEntitySetAnnotations.push(
              makeSimpleCapabilityAnnotation('Capabilities.ExpandRestrictions', {
                Expandable: false,
              })
            )
          }
        } else {
          modelEntitySetAnnotations.push(
            makeSimpleCapabilityAnnotation('Capabilities.ExpandRestrictions', {
              Expandable: false,
            })
          )
        }
      } else {
        modelEntitySetAnnotations.push(
          makeSimpleCapabilityAnnotation('Capabilities.ReadRestrictions', {
            Readable: false,
          })
        )
        if (modelNavigationPropertyMap.size > 0) {
          modelEntitySetAnnotations.push(
            makeSimpleCapabilityAnnotation('Capabilities.ExpandRestrictions', {
              Expandable: false,
            })
          )
        }
      }
      if (modelPolicy.create) {
        modelEntitySetAnnotations.push(
          makeSimpleCapabilityAnnotation(
            'Capabilities.InsertRestrictions',
            stripUndefinedValuesFromObject({
              Insertable: true,
              RequiredProperties:
                insertRequiredProperties.size > 0
                  ? Array.from(insertRequiredProperties)
                  : undefined,
              NonInsertableProperties:
                nonInsertableProperties.size > 0 ? Array.from(nonInsertableProperties) : undefined,
            }) as SimpleCapabilityAnnotationProperties
          )
        )
      } else {
        modelEntitySetAnnotations.push(
          makeSimpleCapabilityAnnotation('Capabilities.InsertRestrictions', {
            Insertable: false,
          })
        )
      }
      if (modelPolicy.update) {
        modelEntitySetAnnotations.push(
          makeSimpleCapabilityAnnotation(
            'Capabilities.UpdateRestrictions',
            stripUndefinedValuesFromObject({
              Updatable: true,
              NonUpdatableProperties:
                nonUpdatableProperties.size > 0 ? Array.from(nonUpdatableProperties) : undefined,
              FilterSegmentSupported: modelPolicy.bulkUpdate,
              TypecastSegmentSupported: modelPolicy.bulkUpdate,
              DeltaUpdateSupported: true,
            } as SimpleCapabilityAnnotationProperties)
          )
        )
      } else {
        modelEntitySetAnnotations.push(
          makeSimpleCapabilityAnnotation('Capabilities.UpdateRestrictions', {
            Updatable: false,
          })
        )
      }
      if (modelPolicy.delete) {
        modelEntitySetAnnotations.push(
          makeSimpleCapabilityAnnotation('Capabilities.DeleteRestrictions', {
            Deletable: true,
          })
        )
      } else {
        modelEntitySetAnnotations.push(
          makeSimpleCapabilityAnnotation('Capabilities.DeleteRestrictions', {
            Deletable: false,
          })
        )
      }
      if (controller) {
        const additional = controller.options?.additional || {}
        Object.entries(additional).forEach(([methodAndPath]) => {
          const [method, path] = methodAndPath.split('|') as [ResourcefulHttpVerb, string]
          if (!method || !path) return
          const opName = makeOpName(method, model.$resourcefulName, path)
          if (!modelOperationSpecMap.has(opName)) {
            const opBinding: Boundness = path.includes(':uid\/') ? 'entity' : 'collection'
            const opKind: OpKind = ['post', 'patch', 'put', 'delete'].includes(method)
              ? 'action'
              : 'function'
            const opSpec: OperationSpec = {
              kind: opKind,
              binding: opBinding,
              name: opName,
              params:
                'action' === opKind
                  ? [{ name: 'payload', type: 'Edm.String', nullable: true }]
                  : [],
              returnType: 'Edm.String',
              entitySetPath: path.replace(/:uid\/?/g, ''),
              isComposable: false,
              enabled: () => true,
            }
            modelOperationSpecMap.set(opName, opSpec)
            modelOperationRestrictions.push({
              PropertyValue: [
                {
                  _attributes: {
                    Property: 'Operation',
                    String: `Default.${opName}`,
                  },
                },
                {
                  _attributes: {
                    Property: 'Target',
                    NavigationPropertyPath: opSpec.entitySetPath!,
                  },
                },
              ],
            })
          }
        })
      }
      // Add the operations to the registry for processing later
      registerModelOps(model, Array.from(modelOperationSpecMap.values()))
      // Add the operation restrictions annotations if needed
      if (modelOperationRestrictions.length > 0) {
        modelEntitySetAnnotations.push({
          _attributes: {
            Term: 'Capabilities.OperationRestrictions',
          },
          Collection: {
            Record: modelOperationRestrictions,
          },
        })
      }
      // Add the model entity set annotations to the global array
      const modelEntitySetAnnotationsElement: ElementCompact = {
        _attributes: {
          Target: annotationsTarget,
        },
        Annotation: modelEntitySetAnnotations,
      }
      AnnotationsArray.push(modelEntitySetAnnotationsElement)
    })
    // Process all registered actions and functions
    const addBoundAction = (
      entityTypeName: string,
      binding: Boundness,
      name: string,
      params: ParamSpec[] = [],
      returnType?: string,
      extras: Partial<ElementCompact['_attributes']> = {},
      annotations?: ElementCompact[]
    ) => {
      const bindingType =
        binding === 'collection' ? `Collection(self.${entityTypeName})` : `self.${entityTypeName}`

      const el: ElementCompact = stripUndefinedValuesFromObject({
        _attributes: { Name: name, IsBound: 'true', ...extras },
        Parameter: [{ _attributes: { Name: 'BindingParameter', Type: bindingType } }].concat(
          params.map((p) =>
            stripUndefinedValuesFromObject({
              _attributes: {
                Name: p.name,
                Type: p.type,
                Nullable: p.nullable ? undefined : 'false',
              },
            })
          )
        ),
        ReturnType: returnType ? { _attributes: { Type: returnType } } : undefined,
        Annotation: annotations && annotations.length ? annotations : undefined,
      })

      const arr = ActionMap.get(entityTypeName) || []
      arr.push(el)
      ActionMap.set(entityTypeName, arr)
    }
    const addBoundFunction = (
      entityTypeName: string,
      binding: Boundness,
      name: string,
      params: ParamSpec[] = [],
      returnType: string,
      opts: {
        isComposable?: boolean
        entitySetPath?: string
        extras?: Partial<ElementCompact['_attributes']>
        annotations?: ElementCompact[]
      } = {}
    ) => {
      const bindingType =
        binding === 'collection' ? `Collection(self.${entityTypeName})` : `self.${entityTypeName}`

      const el: ElementCompact = stripUndefinedValuesFromObject({
        _attributes: stripUndefinedValuesFromObject({
          Name: name,
          IsBound: 'true',
          IsComposable: opts.isComposable ? 'true' : undefined,
          ...(opts.extras || {}),
        }),
        Parameter: [{ _attributes: { Name: 'BindingParameter', Type: bindingType } }].concat(
          params.map((p) =>
            stripUndefinedValuesFromObject({
              _attributes: {
                Name: p.name,
                Type: p.type,
                Nullable: p.nullable ? undefined : 'false',
              },
            })
          )
        ),
        ReturnType: { _attributes: { Type: returnType } },
        Annotation: opts.annotations && opts.annotations.length ? opts.annotations : undefined,
      })

      const arr = FunctionMap.get(entityTypeName) || []
      arr.push(el)
      FunctionMap.set(entityTypeName, arr)
    }
    modelOps.forEach((ops, model) => {
      ops.forEach((op) => {
        if (op.kind === 'action') {
          addBoundAction(
            model.$resourcefulName,
            op.binding,
            op.name,
            op.params || [],
            op.returnType,
            {},
            op.annotations
          )
        } else {
          addBoundFunction(
            model.$resourcefulName,
            op.binding,
            op.name,
            op.params || [],
            op.returnType!,
            {
              isComposable: op.isComposable,
              entitySetPath: op.entitySetPath,
              annotations: op.annotations,
            }
          )
        }
      })
    })
    // Add unbound operations
    Object.entries(this.#opts.additional).forEach(([methodAndPath]) => {
      const [method, path] = methodAndPath.split('|') as [ResourcefulHttpVerb, string]
      if (!method || !path) return
      const opName = makeOpName(method, path)
      const opKind: OpKind = ['post', 'patch', 'put', 'delete'].includes(method)
        ? 'action'
        : 'function'
      const el: ElementCompact = stripUndefinedValuesFromObject({
        _attributes: stripUndefinedValuesFromObject({
          Name: opName,
          ...(opKind === 'function' ? { IsComposable: 'false' } : {}),
        }),
        Parameter:
          opKind === 'action' ? [{ _attributes: { Name: 'Payload', Type: 'Edm.String' } }] : [],
        ReturnType: opKind === 'action' ? undefined : { _attributes: { Type: 'Edm.String' } },
      })
      if (opKind === 'action') {
        const arr = ActionMap.get('Unbound') || []
        arr.push(el)
        ActionMap.set('Unbound', arr)
        ActionImportSet.add(opName)
      } else {
        const arr = FunctionMap.get('Unbound') || []
        arr.push(el)
        FunctionMap.set('Unbound', arr)
        FunctionImportSet.add(opName)
      }
    })
    // Add complex types if needed
    if (hasResourcefulObjectType) {
      ComplexTypeMap.set('ResourcefulObjectType', {
        _attributes: { Name: 'ResourcefulObjectType', OpenType: 'true' },
      })
    }
    ret.EntityType = Array.from(EntityTypeMap.values())
    ret.ComplexType = Array.from(ComplexTypeMap.values())
    ret.EnumType = Array.from(EnumTypeMap.values())
    ret.EntityContainer =
      EntitySetMap.size > 0
        ? stripUndefinedValuesFromObject({
            _attributes: {
              Name: 'Container',
            },
            EntitySet: Array.from(EntitySetMap.values()),
            ActionImport:
              ActionImportSet.size > 0
                ? Array.from(ActionImportSet.values()).map((name) => ({
                    _attributes: {
                      Name: name,
                      Action: `Default.${name}`,
                    },
                  }))
                : undefined,
            FunctionImport:
              FunctionImportSet.size > 0
                ? Array.from(FunctionImportSet.values()).map((name) => ({
                    _attributes: {
                      Name: name,
                      Function: `Default.${name}`,
                      IncludeInServiceDocument: true,
                    },
                  }))
                : undefined,
          })
        : undefined
    ret.Action = Array.from(ActionMap.values()).flat()
    ret.Function = Array.from(FunctionMap.values()).flat()
    ret.Annotations = AnnotationsArray.length > 0 ? AnnotationsArray : undefined
    return stripUndefinedValuesFromObject(ret)
  }

  static async serveRootMetadata(
    opts: ResourcefulRouterOptionsService,
    controllers: Map<string, ResourcefulModelController>,
    ctx: HttpContext
  ): Promise<ElementCompact> {
    const service = new ODataMetadataService(opts, controllers)
    return service.generateMetadataElements(ctx)
  }
}
